
import Vue from "vue";
import moment from "moment";
import eventBus from "@/eventBus";
import OrderList from "./OrderList.vue";
import { processResult } from "@/utils/processResult";
import ResultSummary from "@/components/ResultSummary.vue";
import DateRange from "@/components/DateRange.vue";
import { exportCsv } from "@/utils/csv";
import {
  loadPgFeeRates,
  calculatePgFeeForItem,
  PgFeeCalculation,
  summarizePgFees,
} from "@/utils/pgFee";
import {
  getActualOrderCompositionAmount,
  getInitialOrderCompositionAmount,
  summarizeOrderPayments,
} from "@/utils/orderPaymentSummary";
import {
  filterSettlementResult,
  OrderPathFilter,
  resolveOrderPath,
} from "@/utils/orderPathFilter";
import { summarizeRefundAmounts } from "@/utils/refundAmountSummary";
import {
  getAdjacentSettlementRange,
  SettlementDateRange,
  SettlementPeriodDirection,
} from "@/utils/settlementPeriod";
import {
  formatSettledElapsedTime,
  getSettlementTime,
  SettledElapsedTimeInfo,
} from "@/utils/settlementTimeTracker";

moment.locale("ko");

type BodyScrollLockState = {
  overflow: string;
  paddingRight: string;
};

export default Vue.extend({
  components: {
    OrderList,
    ResultSummary,
    DateRange,
  },
  props: {
    query: Function,
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data: () => ({
    dialog: false,
    isLoading: false,
    isNavigating: false,
    timeNow: Date.now(),
    timeTimer: null as any,
    pgRates: loadPgFeeRates(),
    feeSummary: summarizePgFees([], loadPgFeeRates()),
    sourceResult: null as any,
    bodyScrollLockState: null as BodyScrollLockState | null,
    result: {
      start_date: "",
      end_date: "",
      datetime: "",
      updated_at: undefined as number | undefined,
      payment_composition_amount: 0,
      order_count: 0,
      order_methods: [] as any,
      refund_amount: 0,
      refund_count: 0,
      refund_methods: [] as any,
      payment_summary: summarizeOrderPayments([]),
      orders: [] as any,
      refunds: [] as any,
    },
  }),
  computed: {
    detailSettledTimestamp(): number | null {
      if (
        this.result &&
        typeof (this.result as any).updated_at === "number" &&
        (this.result as any).updated_at > 0
      ) {
        return (this.result as any).updated_at;
      }
      if (
        this.sourceResult &&
        typeof this.sourceResult.updated_at === "number" &&
        this.sourceResult.updated_at > 0
      ) {
        return this.sourceResult.updated_at;
      }
      const mallId = this.$store.state.mall_info?.mall_id;
      const shopNo = this.$store.state.mall_info?.shop_no;
      const start_date =
        this.result.start_date || (this.sourceResult as any)?.start_date;
      const end_date =
        this.result.end_date || (this.sourceResult as any)?.end_date;
      if (mallId && shopNo !== undefined && start_date && end_date) {
        return getSettlementTime(mallId, shopNo, start_date, end_date);
      }
      return null;
    },
    detailSettledInfo(): SettledElapsedTimeInfo {
      return formatSettledElapsedTime(
        this.detailSettledTimestamp,
        this.timeNow
      );
    },
    items(): any[] {
      const items = this.result.orders.concat(this.result.refunds);
      return items;
    },
    groupedItems() {
      const result: Record<string, any> = {};
      this.items.forEach((item) => {
        const order_place = resolveOrderPath(
          item,
          this.orderPathFilter?.presentationMode
        ).label;
        const order_payment_method = String(
          item?.payment_method_name ??
            item?.order?.payment_method_name ??
            "정보 없음"
        );
        const refund_method = Array.isArray(item?.refund_methods)
          ? item.refund_methods[0] == null
            ? "정보 없음"
            : item.refund_methods.toString()
          : "";

        const initial_order_amount = getInitialOrderCompositionAmount(item);
        const actual_order_amount = getActualOrderCompositionAmount(item);
        const refund_amount = summarizeRefundAmounts([item]).amount;

        const key = JSON.stringify([order_place, order_payment_method]);
        if (!result[key]) {
          result[key] = {
            order_place: order_place,
            order_payment_method: order_payment_method,
            refund_method: "",
            refund_methods: [],
            initial_order_amount: 0,
            actual_order_amount: 0,
            refund_amount: 0,
            order_count: 0,
            refund_count: 0,
            items: [],
          };
        }
        if (
          refund_method &&
          !result[key].refund_methods.includes(refund_method)
        ) {
          result[key].refund_methods.push(refund_method);
          result[key].refund_method = result[key].refund_methods.join(", ");
        }
        result[key].initial_order_amount += initial_order_amount;
        result[key].actual_order_amount += actual_order_amount;
        result[key].refund_amount += refund_amount;

        if (Array.isArray(item?.refund_methods)) {
          result[key].refund_count += 1;
        } else {
          result[key].order_count += 1;
        }
        result[key].items.push(item);
      });

      return Object.values(result);
    },
    groupedItemsTotalOrderAmount(): number {
      return this.groupedItems.reduce((sum, item) => {
        const amount = parseInt(item?.actual_order_amount);
        return sum + amount;
      }, 0);
    },
    groupedItemsTotalCompositionAmount(): number {
      return this.groupedItems.reduce((sum, item) => {
        const amount = parseInt(item?.initial_order_amount);
        return sum + amount;
      }, 0);
    },
    groupedItemsTotalRefundAmount(): number {
      return this.groupedItems.reduce((sum, item) => {
        const amount = parseInt(item?.refund_amount);
        return sum + amount;
      }, 0);
    },
    totalPgFee(): number {
      return this.groupedItems.reduce(
        (sum, item) => sum + this.getPgFee(item),
        0
      );
    },
    totalNetSettlement(): number {
      return (
        this.groupedItemsTotalOrderAmount +
        this.groupedItemsTotalRefundAmount -
        this.totalPgFee
      );
    },
    previousPeriodRange(): SettlementDateRange | null {
      return getAdjacentSettlementRange(
        this.result.start_date,
        this.result.end_date,
        -1
      );
    },
    nextPeriodRange(): SettlementDateRange | null {
      return getAdjacentSettlementRange(
        this.result.start_date,
        this.result.end_date,
        1
      );
    },
  },
  watch: {
    dialog(value: boolean) {
      if (value) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }
    },
    orderPathFilter: {
      handler() {
        if (this.sourceResult) this.applyOrderPathFilter();
      },
      deep: true,
    },
  },
  methods: {
    formatDifference(amount: number): string {
      if (amount === 0) return "0원";
      return `${amount > 0 ? "+" : "-"}${Math.abs(amount).toLocaleString()}원`;
    },
    getPgFee(item: any): number {
      return this.getFeeCalculation(item).fee;
    },
    getFeeCalculation(item: any): PgFeeCalculation {
      const sourceItems = Array.isArray(item?.items)
        ? item.items.filter(
            (source: any) => !Array.isArray(source?.refund_methods)
          )
        : [item];
      if (sourceItems.length === 0) {
        return {
          fee: 0,
          status: "exact",
          breakdown: {
            components: [],
            totalAmount: 0,
            status: "exact",
            source: "none",
          },
          componentFees: [],
        };
      }

      const calculations: PgFeeCalculation[] = sourceItems.map((source: any) =>
        calculatePgFeeForItem(
          source,
          this.pgRates,
          source,
          this.orderPathFilter?.presentationMode
        )
      );
      const status = calculations.some(
        (calculation) => calculation.status === "unavailable"
      )
        ? "unavailable"
        : calculations.some((calculation) => calculation.status === "estimated")
        ? "estimated"
        : "exact";
      return {
        fee: calculations.reduce(
          (sum, calculation) => sum + calculation.fee,
          0
        ),
        status,
        breakdown: calculations[0].breakdown,
        componentFees: calculations.reduce(
          (all, calculation) => all.concat(calculation.componentFees),
          [] as PgFeeCalculation["componentFees"]
        ),
        reason: calculations.find((calculation) => calculation.reason)?.reason,
      };
    },
    getFeeStatus(item: any): string {
      if (item.order_count === 0) return "refund";
      return this.getFeeCalculation(item).status;
    },
    getNetSettlement(item: any): number {
      const gross = item.actual_order_amount + item.refund_amount;
      return gross - this.getPgFee(item);
    },
    downloadExcel() {
      const headers = [
        "주문경로",
        "결제방식",
        "환불방식",
        "결제수",
        "환불수",
        "결제 합계",
        "현재 결제 합계",
        "차이 금액",
        "환불 합계",
        "정산금액(전)",
        "수수료율",
        "예상 PG수수료",
        "계산상태",
        "결제구성",
        "예상 순정산액",
      ];
      const rows = this.groupedItems.map((item: any) => [
        item.order_place,
        item.order_payment_method,
        item.refund_method,
        item.order_count,
        item.refund_count,
        item.initial_order_amount,
        item.actual_order_amount,
        item.initial_order_amount - item.actual_order_amount,
        Math.abs(item.refund_amount),
        item.actual_order_amount + item.refund_amount,
        this.getFeeCalculation(item)
          .componentFees.map(
            (component) => `${component.name} ${component.effectiveRate}%`
          )
          .join(" + "),
        this.getPgFee(item),
        this.getFeeStatus(item),
        this.getFeeCalculation(item)
          .breakdown.components.map((component) =>
            component.amount === undefined
              ? component.name
              : `${component.name} ${component.amount.toLocaleString()}원`
          )
          .join(" + "),
        this.getNetSettlement(item),
      ]);
      const filename = `정산내역_${this.result.start_date}_${this.result.end_date}.csv`;
      exportCsv(headers, rows, filename);
      eventBus.$emit("snackbar", {
        message: "📥 엑셀 파일(CSV) 다운로드가 완료되었습니다.",
      });
    },
    printReport() {
      window.print();
    },
    refreshResult() {
      if (!this.result.start_date || !this.result.end_date) return;
      if (this.detailSettledInfo.isJustSettled) {
        eventBus.$emit("snackbar", {
          message:
            "방금 정산을 완료한 최신 데이터입니다. 약 1분 후 다시 정산할 수 있습니다.",
        });
        return;
      }
      if (typeof this.query === "function") {
        this.query(this.result.start_date, this.result.end_date, true, false);
      } else {
        eventBus.$emit(
          "query",
          this.result.start_date,
          this.result.end_date,
          true,
          false
        );
      }
    },
    async navigatePeriod(direction: SettlementPeriodDirection) {
      if (this.isNavigating) return;

      const target =
        direction === -1 ? this.previousPeriodRange : this.nextPeriodRange;
      if (!target) return;

      this.isNavigating = true;
      try {
        if (typeof this.query === "function") {
          await this.query(target.start, target.end, false, true);
        } else {
          eventBus.$emit("query", target.start, target.end, false, true);
        }
      } finally {
        this.isNavigating = false;
      }
    },
    handleOrderGroupOpen(item: any) {
      this.$emit("open-orders", {
        startDate: this.result.start_date,
        endDate: this.result.end_date,
        place: item.order_place,
        method: item.order_payment_method,
        updateRoute: true,
      });
    },
    handleAllOrdersOpen() {
      this.$emit("open-orders", {
        startDate: this.result.start_date,
        endDate: this.result.end_date,
        updateRoute: true,
      });
    },
    handleOrderListClose() {
      this.$emit("close-orders", {
        startDate: this.result.start_date,
        endDate: this.result.end_date,
      });
    },
    hasDisplayedResult(
      mallId: string,
      shopNo: string | number,
      startDate: string,
      endDate: string
    ): boolean {
      if (!this.dialog || !this.sourceResult) return false;

      const sourceStart =
        this.sourceResult.start_date || this.sourceResult.date;
      const sourceEnd = this.sourceResult.end_date || this.sourceResult.date;
      return (
        String(this.sourceResult.mall_id || "") === String(mallId || "") &&
        String(this.sourceResult.shop_no ?? "") === String(shopNo ?? "") &&
        sourceStart === startDate &&
        sourceEnd === endDate
      );
    },
    syncOrderListRoute(filter?: {
      place?: string;
      method?: string;
      refund?: string;
    }) {
      this.$nextTick(() => {
        if (filter) {
          this.autoOpenOrderList(filter);
        } else {
          (this.$refs.orderList as any)?.collapseAll(false);
        }
      });
    },
    lockBodyScroll() {
      if (typeof window === "undefined" || this.bodyScrollLockState) return;

      const body = document.body;
      const scrollbarWidth = Math.max(
        0,
        window.innerWidth - document.documentElement.clientWidth
      );
      const computedPaddingRight =
        parseFloat(window.getComputedStyle(body).paddingRight) || 0;

      this.bodyScrollLockState = {
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight,
      };
      body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${computedPaddingRight + scrollbarWidth}px`;
      }
    },
    unlockBodyScroll() {
      if (typeof window === "undefined" || !this.bodyScrollLockState) return;

      const body = document.body;
      body.style.overflow = this.bodyScrollLockState.overflow;
      body.style.paddingRight = this.bodyScrollLockState.paddingRight;
      this.bodyScrollLockState = null;
    },
    applyOrderPathFilter() {
      if (!this.sourceResult) return;
      const filtered = filterSettlementResult(
        this.sourceResult,
        this.orderPathFilter
      );
      const start_date = filtered.date || filtered.start_date;
      const end_date = filtered.date || filtered.end_date;
      const result = processResult({
        mall_id: filtered.mall_id,
        shop_no: filtered.shop_no,
        start_date,
        end_date,
        orders: filtered.orders,
        refunds: filtered.refunds,
        updated_at: filtered.updated_at ?? this.sourceResult?.updated_at,
      });
      this.result = result;
      this.feeSummary = summarizePgFees(
        result.orders,
        this.pgRates,
        this.orderPathFilter?.presentationMode
      );
    },
    autoOpenOrderList(filter: {
      place?: string;
      method?: string;
      refund?: string;
    }) {
      const orderList = this.$refs.orderList as any;
      if (!filter.place && !filter.method) {
        orderList?.expandAll(false);
        return;
      }
      let target = this.groupedItems.find((item: any) => {
        const matchPlace = !filter.place || item.order_place === filter.place;
        const matchMethod =
          !filter.method || item.order_payment_method === filter.method;
        return matchPlace && matchMethod;
      });
      if (!target && this.groupedItems.length > 0) {
        target = this.groupedItems.find((item: any) => {
          return (
            (filter.place && item.order_place === filter.place) ||
            (filter.method && item.order_payment_method === filter.method)
          );
        });
      }
      if (target) {
        orderList?.expandGroup(target, false);
      } else if (this.groupedItems.length > 0) {
        orderList?.expandGroup(this.groupedItems[0], false);
      }
    },
    async open(
      d: any,
      autoOrderFilter?: { place?: string; method?: string; refund?: string }
    ) {
      this.isLoading = true;
      this.pgRates = loadPgFeeRates();
      try {
        this.sourceResult = d;
        this.applyOrderPathFilter();
        this.dialog = true;
        if (autoOrderFilter) {
          this.$nextTick(() => {
            this.autoOpenOrderList(autoOrderFilter);
          });
        }
      } catch (e) {
        console.error("ResultOne open error:", e);
      } finally {
        this.isLoading = false;
      }
    },
    close() {
      this.dialog = false;
      this.$emit("close");
    },
    closeSilently() {
      this.dialog = false;
    },
  },
  created() {
    eventBus.$on("pg-fee-updated", () => {
      this.pgRates = loadPgFeeRates();
      this.feeSummary = summarizePgFees(
        this.result.orders,
        this.pgRates,
        this.orderPathFilter?.presentationMode
      );
    });
  },
  mounted() {
    this.timeTimer = setInterval(() => {
      this.timeNow = Date.now();
    }, 10000);
  },
  beforeDestroy() {
    eventBus.$off("pg-fee-updated");
    this.unlockBodyScroll();
    if (this.timeTimer) {
      clearInterval(this.timeTimer);
      this.timeTimer = null;
    }
  },
});
